import type { AnalyzerStage, ResumedFrom } from "../../api/types";

/**
 * The pipeline in order, with the names an operator picking a stage reads.
 *
 * The order is load-bearing twice over — the re-run dialog asks which stages a
 * run can resume past, the report asks which of its rows a resumed run
 * inherited — and both questions are "is this stage before that one", which
 * only a single list can answer consistently.
 */
export const ANALYZER_STAGES: Array<{ id: AnalyzerStage; display: string }> = [
  { id: "cleanup", display: "Cleanup" },
  { id: "annotator", display: "Annotator" },
  { id: "lift-to-l1", display: "Lift L1 (Actions)" },
  { id: "lift-to-l2", display: "Lift L2 (Activities)" },
  { id: "lift-to-l3", display: "Lift L3 (Process-steps)" },
  { id: "lift-to-l4", display: "Lift L4 (Cases)" },
  { id: "summarize", display: "Summarize (Session summary)" },
  { id: "adapter", display: "Adapter" },
];

/** A stage's position in the pipeline; `-1` for a name this build does not know. */
export const stageIndex = (stage: AnalyzerStage) =>
  ANALYZER_STAGES.findIndex(known => known.id === stage);

/**
 * The hierarchy level each lift produces, so a level row can be traced back to
 * the stage that lifted it — the only thing that decides whether its nodes were
 * inherited.
 */
const LEVEL_STAGE: Record<number, AnalyzerStage> = {
  1: "lift-to-l1",
  2: "lift-to-l2",
  3: "lift-to-l3",
  4: "lift-to-l4",
};

/**
 * Whether `stage` was copied from the base run rather than executed by this one.
 *
 * A resumed run runs `startStage` and everything after it; every earlier stage
 * is a copy of the base run's artifact and fragment. `null` — a full run, or a
 * stored report from before the lineage was recorded — inherits nothing, which
 * is the reading that leaves the page saying nothing rather than guessing.
 *
 * A stage neither list knows (an older report naming a stage this build dropped)
 * reads as not inherited for the same reason.
 */
export const isStageInherited = (
  resumedFrom: ResumedFrom | null | undefined,
  stage: AnalyzerStage,
): boolean => {
  if (!resumedFrom) return false;
  const start = stageIndex(resumedFrom.startStage);
  const own = stageIndex(stage);
  return start >= 0 && own >= 0 && own < start;
};

/**
 * Whether a hierarchy level's nodes came from the base run. The level's stats
 * are always recomputed by this run's Adapter, but over nodes the inherited
 * lift produced — so the chip marks the nodes, not the arithmetic.
 */
export const isLevelInherited = (
  resumedFrom: ResumedFrom | null | undefined,
  depth: number,
): boolean => {
  const stage = LEVEL_STAGE[depth];
  return stage ? isStageInherited(resumedFrom, stage) : false;
};
